import React from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Layout,
  notification,
  Select,
  Skeleton,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { AppEnum } from '@enums/app.enum';
import emailjs from '@emailjs/browser';
import { EmailSourceEnum, EmailTemplateDisplayEnum } from '@enums/email.enum';

interface ArWithdrawalCancellationValues {
  details: string;
  documentNumber: string;
  email: string;
  fullName: string;
  requestType: string;
}

export const ArWithdrawalCancellationPage: React.FC = () => {
  const { t } = useTranslation();

  const requestTypeOptions = [
    {
      label: t('legal.arWithdrawalCancellation.form.options.withdrawal'),
      value: 'withdrawal',
    },
    {
      label: t('legal.arWithdrawalCancellation.form.options.cancellation'),
      value: 'cancellation',
    },
  ];

  const [api, contextHolder] = notification.useNotification();

  const [form] = Form.useForm<ArWithdrawalCancellationValues>();

  const [loading, setLoading] = React.useState(false);

  const onFinish = (values: ArWithdrawalCancellationValues) => {
    setLoading(true);

    emailjs
      .send(
        AppEnum.emailServiceId,
        AppEnum.emailTemplateId,
        {
          ...values,
          ar_display: EmailTemplateDisplayEnum.TableRow,
          contact_display: EmailTemplateDisplayEnum.None,
          message: '',
          source: EmailSourceEnum.ArWithdrawalCancellation,
        },
        AppEnum.emailPublicKey
      )
      .then(
        () => {
          setLoading(false);

          api.success({
            description: t(
              'legal.arWithdrawalCancellation.form.successDescription'
            ),
            message: t('legal.arWithdrawalCancellation.form.successTitle'),
            placement: 'bottomRight',
          });

          form.resetFields();
        },
        () => {
          setLoading(false);

          api.error({
            description: t('error.contactUsError'),
            message: t('error.contactUsErrorTitle'),
            placement: 'bottomRight',
          });
        }
      );
  };

  return (
    <Layout className="min-h-screen bg-[aliceblue] overflow-x-hidden">
      {contextHolder}
      <Layout.Content className="bg-no-hidden-fees bg-no-repeat bg-cover mt-[200px] mb-[30px] px-4 md:px-8 lg:px-16">
        <Skeleton loading={loading} active>
          <div className="flex justify-center">
            <section className="w-full max-w-[900px] border border-[#D0E3ED] p-6 md:p-10 rounded-2xl">
              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 pb-4 break-words">
                  {t('legal.arWithdrawalCancellation.title')}
                </h2>
                <p className="text-gray-700 mb-4">
                  <strong className="text-gray-900">
                    {t(
                      'legal.arWithdrawalCancellation.form.options.withdrawal'
                    )}
                    :
                  </strong>{' '}
                  {t('legal.arWithdrawalCancellation.withdrawalDescription')}
                </p>
                <p className="text-gray-700">
                  <strong className="text-gray-900">
                    {t(
                      'legal.arWithdrawalCancellation.form.options.cancellation'
                    )}
                    :
                  </strong>{' '}
                  {t('legal.arWithdrawalCancellation.cancellationDescription')}
                </p>
              </div>
              <Card className="rounded-xl bg-white shadow-sm border border-[#E6EEF3]">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <Form.Item
                    name="fullName"
                    label={t('legal.arWithdrawalCancellation.form.fullName')}
                    rules={[
                      {
                        message: t(
                          'legal.arWithdrawalCancellation.form.errors.fullName'
                        ),
                        required: true,
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'legal.arWithdrawalCancellation.form.fullName'
                      )}
                      size="large"
                      className="rounded"
                    />
                  </Form.Item>
                  <Form.Item
                    name="documentNumber"
                    label={t(
                      'legal.arWithdrawalCancellation.form.documentNumber'
                    )}
                    rules={[
                      {
                        message: t(
                          'legal.arWithdrawalCancellation.form.errors.documentNumber'
                        ),
                        required: true,
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'legal.arWithdrawalCancellation.form.documentNumber'
                      )}
                      size="large"
                      className="rounded"
                    />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label={t('legal.arWithdrawalCancellation.form.email')}
                    rules={[
                      {
                        message: t(
                          'legal.arWithdrawalCancellation.form.errors.email'
                        ),
                        required: true,
                      },
                      { type: 'email' },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'legal.arWithdrawalCancellation.form.email'
                      )}
                      size="large"
                      className="rounded"
                    />
                  </Form.Item>
                  <Form.Item
                    name="requestType"
                    label={t('legal.arWithdrawalCancellation.form.requestType')}
                    rules={[
                      {
                        message: t(
                          'legal.arWithdrawalCancellation.form.errors.requestType'
                        ),
                        required: true,
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'legal.arWithdrawalCancellation.form.requestType'
                      )}
                      options={requestTypeOptions}
                      size="large"
                      className="rounded"
                    />
                  </Form.Item>
                  <Form.Item
                    name="details"
                    label={t('legal.arWithdrawalCancellation.form.details')}
                    rules={[
                      {
                        message: t(
                          'legal.arWithdrawalCancellation.form.errors.details'
                        ),
                        required: true,
                      },
                    ]}
                    className="md:col-span-2"
                  >
                    <Input.TextArea
                      placeholder={t(
                        'legal.arWithdrawalCancellation.form.detailsPlaceholder'
                      )}
                      rows={5}
                      className="rounded"
                    />
                  </Form.Item>
                  <div className="md:col-span-2 flex justify-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      className="bg-[#005FCC] font-semibold font-archivo"
                    >
                      {t('legal.arWithdrawalCancellation.form.submit')}
                    </Button>
                  </div>
                </Form>
              </Card>
            </section>
          </div>
        </Skeleton>
      </Layout.Content>
    </Layout>
  );
};
